import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, Eye, EyeOff, Lock, Shield, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import PublicLayout from '@/components/PublicLayout';
import { cn } from '@/lib/utils';

const trustItems = [
  {
    icon: Lock,
    title: 'Bank-grade encryption',
    description: 'Your data is encrypted at rest and in transit.',
  },
  {
    icon: Shield,
    title: 'Trusted platform',
    description: 'Join thousands managing their wealth with Hoito.',
  },
  {
    icon: Wallet,
    title: 'Your data, yours',
    description: 'Export anytime. We never sell your information.',
  },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user, loading, initialized } = useAuth();

  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname ?? '/dashboard';

  useEffect(() => {
    if (initialized && user && !isSubmitting) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from, initialized, isSubmitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand-900 border-t-transparent" />
      </div>
    );
  }

  if (user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand-900 border-t-transparent" />
      </div>
    );
  }

  return (
    <PublicLayout>
      <main className="pt-20 pb-14 lg:pt-28 lg:pb-20 min-h-[calc(100vh-200px)] flex items-center">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Form section */}
            <div className="order-2 lg:order-1">
              <div
                className={cn(
                  'max-w-md mx-auto lg:mx-0',
                  'animate-fade-in'
                )}
              >
                <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm hover:shadow-lg hover:border-slate-300/80 transition-all duration-300 p-6 sm:p-8">
                  <div className="mb-6">
                    <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                      Welcome back
                    </h1>
                    <p className="mt-2 text-slate-600">
                      Sign in to access your dashboard and manage your wealth.
                    </p>
                  </div>

                  {error && (
                    <Alert
                      variant="destructive"
                      className="mb-6 border-red-200 bg-red-50"
                    >
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-red-800">{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-900 font-medium">
                        Email address
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className={cn(
                          'h-11 border-slate-200 text-slate-900 placeholder:text-slate-400',
                          'focus-visible:ring-2 focus-visible:ring-brand-900/20 focus-visible:border-brand-900',
                          'transition-all duration-200 rounded-lg'
                        )}
                        required
                        autoComplete="email"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-slate-900 font-medium">
                          Password
                        </Label>
                      </div>
                      <div className="relative group">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          className={cn(
                            'h-11 pr-11 border-slate-200 text-slate-900 placeholder:text-slate-400',
                            'focus-visible:ring-2 focus-visible:ring-brand-900/20 focus-visible:border-brand-900',
                            'transition-all duration-200 rounded-lg'
                          )}
                          required
                          autoComplete="current-password"
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={cn(
                            'absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md',
                            'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
                            'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-900/20 focus:ring-offset-1'
                          )}
                          tabIndex={-1}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="pt-1">
                      <Button
                        type="submit"
                        className={cn(
                          'w-full h-11 rounded-lg font-medium',
                          'bg-brand-900 hover:bg-brand-800 text-white',
                          'shadow-sm hover:shadow-md active:scale-[0.99]',
                          'transition-all duration-200'
                        )}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Signing in...
                          </span>
                        ) : (
                          'Sign in'
                        )}
                      </Button>
                    </div>
                  </form>

                  <p className="mt-6 text-center text-sm text-slate-600">
                    Don&apos;t have an account?{' '}
                    <Link
                      to="/register"
                      className="font-medium text-brand-900 hover:text-brand-800 transition-colors underline-offset-2 hover:underline"
                    >
                      Create account
                    </Link>
                  </p>
                </div>

                {/* Mobile trust badges */}
                <div className="lg:hidden mt-6 flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    Bank-grade security
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    No credit card
                  </span>
                </div>
              </div>
            </div>

            {/* Trust panel - desktop */}
            <div className="hidden lg:block order-1 lg:order-2">
              <div className="max-w-md lg:ml-auto space-y-6 animate-fade-in">
                <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-brand-50/30 border border-slate-200/80 p-8">
                  <h2 className="text-lg font-semibold text-slate-900">
                    Your financial data, secured
                  </h2>
                  <p className="mt-2 text-slate-600 text-sm">
                    Hoito keeps your assets and liabilities private with industry-standard security.
                  </p>
                  <div className="mt-6 space-y-4">
                    {trustItems.map((item, i) => (
                      <div
                        key={item.title}
                        className="flex gap-4 items-start p-3 rounded-xl bg-white/60 border border-slate-200/60 hover:bg-white hover:border-slate-300/80 transition-all duration-200"
                        style={{ animationDelay: `${100 + i * 50}ms` }}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand-900 flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900 text-sm">{item.title}</h3>
                          <p className="mt-0.5 text-slate-600 text-xs">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    No credit card
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    256-bit encryption
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PublicLayout>
  );
}
